import React, { useEffect, useState } from "react";
import { Trash2, Loader2, Settings, CheckCircle2, Calendar, Plug } from "lucide-react";
import { toast } from "sonner";
import { destinationConnectors } from "./data";
import { Connector } from "../../../../components/types";
import { useNavigate } from "react-router-dom";
import { SetupGuide } from "../../../../components/marketing/connector/setup-guide";
import { ConnectorSelection } from "../../../../components/marketing/connector/connector-seletion";
import { useDestinations, useDeleteDestination, AirbyteConnector } from "./airbyte-api";

// Matches a real connected destination back to its catalog entry (for its
// emoji icon + a readable type name) by `definitionId` first — the one field
// that's unambiguous — falling back to a slug match against `destinationType`
// (Airbyte's public API returns this as the connector's docker repo short
// name, which is exactly how this catalog's own `id`s are already written).
const resolveCatalogEntry = (destination: AirbyteConnector): Connector | undefined =>
  destinationConnectors.find((c) => c.definitionId && c.definitionId === destination.definitionId) ||
  destinationConnectors.find((c) => c.id.toLowerCase() === (destination.destinationType || "").toLowerCase());

// `createdAt` isn't documented as seconds vs. milliseconds anywhere in this
// wrapper's spec — Airbyte's own public API uses epoch seconds, so this
// treats anything under the year-2001-in-ms threshold as seconds.
const formatConnectedDate = (createdAt?: number): string | null => {
  if (!createdAt) return null;
  const ms = createdAt < 1e12 ? createdAt * 1000 : createdAt;
  const d = new Date(ms);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const DestinationsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDestination, setSelectedDestination] = useState<Connector | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showGuide, setShowGuide] = useState(false);
  const [selectedConnectorForGuide, setSelectedConnectorForGuide] = useState<Connector | null>(null);
  const [existingDestinationForGuide, setExistingDestinationForGuide] = useState<AirbyteConnector | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AirbyteConnector | null>(null);

  // Real GET /{org}/destinations — this tenant's own created destinations,
  // separate from the catalog of connector *types* below (destinationConnectors).
  const { data: destinations, loading: destinationsLoading, error: destinationsError, reload: reloadDestinations } = useDestinations(true);
  const { remove: deleteDestination, loading: deleting } = useDeleteDestination();

  // Per direct ask: a failed load surfaces as a toast (the backend's own
  // message), not a persistent banner sitting above the rest of the page.
  useEffect(() => {
    if (destinationsError) toast.error(destinationsError);
  }, [destinationsError]);

  const filteredConnectors = destinationConnectors.filter((connector) => connector.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleConfigure = (id: string) => {};

  function handleAddConnector() {
    navigate("/track/connections/new");
  }

  const handleCloseGuide = (): void => {
    setShowGuide(false);
    setSelectedConnectorForGuide(null);
    setExistingDestinationForGuide(null);
  };

  // `existing` is only set from the "Configure" button on an
  // already-connected destination — its real `.configuration` prefills the
  // form instead of showing schema defaults.
  const handleShowGuide = (connector: Connector, existing?: AirbyteConnector): void => {
    setSelectedConnectorForGuide(connector);
    setExistingDestinationForGuide(existing || null);
    setShowGuide(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleDelete = async (destination: AirbyteConnector) => {
    const { ok, error } = await deleteDestination(destination.destinationId!);
    setPendingDelete(null);
    if (ok) {
      toast.success(`${destination.name} disconnected.`);
      reloadDestinations();
    } else {
      toast.error(error || `Couldn't disconnect ${destination.name}.`);
    }
  };

  return (
    <div className="min-h-screen my-5 bg-white dark:bg-gray-900 flex">
      <div className={`transition-all duration-300 px-6 ${showGuide ? "w-1/2" : "w-full"}`}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Destinations</h1>
          </div>
          <button onClick={handleAddConnector} className="px-4 mt-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
            Add Destinations
          </button>
        </div>

        {/* Real connected destinations — GET /{org}/destinations */}
        {destinationsLoading && destinations.length === 0 ? (
          <p className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 mb-6">
            <Loader2 size={14} className="animate-spin" /> Loading your destinations…
          </p>
        ) : destinations.length > 0 ? (
          <div className="mb-8">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Connected Destinations <span className="text-gray-400 dark:text-gray-500 font-normal">({destinations.length})</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {destinations.map((d) => {
                const catalogEntry = resolveCatalogEntry(d);
                const dateLabel = formatConnectedDate(d.createdAt);
                return (
                  <div
                    key={d.destinationId}
                    className="flex items-start justify-between gap-3 p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-gray-200 dark:hover:border-gray-700 transition-colors"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-xl shrink-0">
                        {catalogEntry?.icon || <Plug size={16} className="text-gray-400" />}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{d.name}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{catalogEntry?.name || d.destinationType}</p>
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
                          <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 size={11} /> Connected
                          </span>
                          {dateLabel && (
                            <span className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                              <Calendar size={11} /> {dateLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {catalogEntry && (
                        <button
                          onClick={() => handleShowGuide(catalogEntry, d)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                          title="Configure"
                        >
                          <Settings size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => setPendingDelete(d)}
                        disabled={deleting}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-50"
                        title="Disconnect"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">Available Destinations • {destinationConnectors.length}+ integrations</p>
        </div>

        <ConnectorSelection
          title="Select Destinations"
          description="Choose a data destination to connect from"
          connectors={filteredConnectors}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          selectedConnector={selectedDestination}
          onSelectConnector={setSelectedDestination}
          onShowGuide={handleShowGuide}
          onConfigure={handleConfigure}
          placeholder="Search destinations..."
          compact={showGuide}
        />
      </div>

      {showGuide && selectedConnectorForGuide && (
        <div className="w-1/2 border-l border-gray-300 dark:border-gray-700">
          <SetupGuide
            connector={selectedConnectorForGuide}
            onClose={handleCloseGuide}
            onSaved={reloadDestinations}
            existingConfiguration={existingDestinationForGuide?.configuration}
            existingName={existingDestinationForGuide?.name}
          />
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !deleting && setPendingDelete(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Disconnect {pendingDelete.name}?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
              This stops syncing data to this destination and can't be undone. You'll need to reconnect it from scratch if you change your mind.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(pendingDelete)}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50"
              >
                {deleting && <Loader2 size={13} className="animate-spin" />}
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DestinationsDashboard;
